import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Modal, Button, Carousel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth0 } from '@auth0/auth0-react';
import { ArrowLeftRight, Bitcoin, Bookmark, Flag, HandHeart, PackagePlus, ShieldCheck, ShoppingBasket, Sparkles, Store, WalletCards } from 'lucide-react';
import BuyNowModal from './BuyNowModal';
import { useFetchAssets } from '../../../hooks/DataFetching/useFetchAssets';
import TradeSelectionModal from './TradeSelectionModal';
import TradeConfirmationModal from './TradeConfirmationModal';
import ItemsList from './ItemsList';
import StripeOffers from './StripeOffers';
import { purchaseAssetWithSheaf } from '../../../Services/SheafService';
import { StatusMessage } from '../../ui/StatusMessage';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../../config/featureFlags';
import { useSavedMarketplaceListings } from '../../../hooks/DataFetching/useSavedMarketplaceListings';
import '../../../styles/components/ItemsList.css';

const normalizeText = (value) => String(value || '').trim().toLowerCase();
const normalizeUserId = (value) => String(value || '').replace(/[|-]/g, '').trim();

const priceValue = (asset) => {
  const parsed = Number(asset?.salePrice);
  return Number.isFinite(parsed) ? parsed : 0;
};

const ForSalePage = () => {
  const { groupId, sellerId } = useParams();
  const history = useHistory();
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [tradeSubmitting, setTradeSubmitting] = useState(false);
  const [tradeError, setTradeError] = useState(null);
  const [tradeSuccess, setTradeSuccess] = useState(null);
  const [sheafSubmitting, setSheafSubmitting] = useState(false);
  const [sheafError, setSheafError] = useState(null);
  const [sheafSuccess, setSheafSuccess] = useState(null);
  const [testDataSubmitting, setTestDataSubmitting] = useState(false);
  const [testDataError, setTestDataError] = useState(null);
  const [testDataSuccess, setTestDataSuccess] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const testDataToolsEnabled = isFeatureEnabled(FEATURE_FLAGS.testDataTools);
    const bitcoinEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience);
  const barterEnabled = isFeatureEnabled(FEATURE_FLAGS.marketplaceBarter);
  const stripeEnabled = isFeatureEnabled(FEATURE_FLAGS.marketplaceStripe);
  const sheafEnabled = isFeatureEnabled(FEATURE_FLAGS.sheafPurchase);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [sortMode, setSortMode] = useState('featured');
  const [sellerReviewSummary, setSellerReviewSummary] = useState(null);
  const [sellerReviewError, setSellerReviewError] = useState(null);
  const [storefrontReviewSummary, setStorefrontReviewSummary] = useState(null);
  const [storefrontReviewError, setStorefrontReviewError] = useState(null);
  const [sellerReviewSummaries, setSellerReviewSummaries] = useState({});
  const [reportAsset, setReportAsset] = useState(null);
  const [reportReason, setReportReason] = useState('misleading-listing');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportSuccess, setReportSuccess] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [reportHistoryLoading, setReportHistoryLoading] = useState(false);
  const [reportHistoryError, setReportHistoryError] = useState(null);
  const [withdrawingReportId, setWithdrawingReportId] = useState(null);
  const normalizedSellerId = normalizeUserId(sellerId);
  const { assets, loading: assetsLoading, error: assetsError, refetch: refetchAssets } = useFetchAssets(groupId, showAddModal);
  const sellerOwnerKey = useMemo(() => (
    [...new Set((Array.isArray(assets) ? assets : [])
      .map((asset) => normalizeUserId(asset.ownedByUserId || asset.ownerId))
      .filter(Boolean))]
      .sort()
      .join('|')
  ), [assets]);
  const {
    savedAssetIds,
    savingAssetId,
    error: savedListingsError,
    toggleSavedListing,
  } = useSavedMarketplaceListings({
    apiUrl,
    getAccessTokenSilently,
    enabled: !groupId,
  });

  const loadMyMarketplaceReports = useCallback(async () => {
    if (!apiUrl || !user?.sub) {
      setReportHistory([]);
      return;
    }

    setReportHistoryLoading(true);
    setReportHistoryError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${apiUrl}/api/MarketplaceReports/mine`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Marketplace report history failed (${response.status})`);
      }

      const reports = await response.json();
      setReportHistory(Array.isArray(reports) ? reports : []);
    } catch (error) {
      console.error('Error loading marketplace report history:', error);
      setReportHistoryError('Could not load your marketplace safety history.');
    } finally {
      setReportHistoryLoading(false);
    }
  }, [apiUrl, getAccessTokenSilently, user?.sub]);

  useEffect(() => {
    loadMyMarketplaceReports();
  }, [loadMyMarketplaceReports]);

  const marketplaceSummary = useMemo(() => {
    const activeAssets = Array.isArray(assets) ? assets : [];
    return {
      total: activeAssets.length,
      btc: activeAssets.filter((asset) => asset.ownedByGroupId && asset.forSale).length,
      trade: activeAssets.filter((asset) => asset.tradable).length,
      sheaf: activeAssets.filter((asset) => asset.ownedByGroupId && asset.acceptsSheaf).length,
    };
  }, [assets]);

  const visibleAssets = useMemo(() => {
    const query = normalizeText(searchTerm);
    const filtered = (Array.isArray(assets) ? assets : []).filter((asset) => {
      const ownerId = normalizeUserId(asset.ownedByUserId || asset.ownerId);
      const ownerReviewSummary = sellerReviewSummaries[ownerId];
      const searchableText = normalizeText([
        asset.productName,
        asset.assetName,
        asset.productDescription,
        asset.assetDescription,
        asset.location,
      ].filter(Boolean).join(' '));

      const matchesSeller = !normalizedSellerId || ownerId === normalizedSellerId;
      const matchesSearch = !query || searchableText.includes(query);
      const matchesFilter =
        paymentFilter === 'all' ||
        (paymentFilter === 'free' && asset.isFree) ||
        (bitcoinEnabled && asset.ownedByGroupId && paymentFilter === 'btc' && asset.forSale) ||
        (barterEnabled && paymentFilter === 'trade' && asset.tradable) ||
        (sheafEnabled && asset.ownedByGroupId && paymentFilter === 'sheaf' && asset.acceptsSheaf) ||
        (paymentFilter === 'saved' && savedAssetIds.has(Number(asset.id))) ||
        (paymentFilter === 'reviewed' && Number(ownerReviewSummary?.reviewCount || 0) > 0);

      return matchesSeller && matchesSearch && matchesFilter;
    });

    return [...filtered].sort((a, b) => {
      if (sortMode === 'price-low') return priceValue(a) - priceValue(b);
      if (sortMode === 'price-high') return priceValue(b) - priceValue(a);
      if (sortMode === 'seller-rating') {
        const sellerA = sellerReviewSummaries[normalizeUserId(a.ownedByUserId || a.ownerId)] || {};
        const sellerB = sellerReviewSummaries[normalizeUserId(b.ownedByUserId || b.ownerId)] || {};
        return Number(sellerB.averageRating || 0) - Number(sellerA.averageRating || 0);
      }
      if (sortMode === 'name') {
        return String(a.productName || a.assetName || '').localeCompare(String(b.productName || b.assetName || ''));
      }

      const score = (asset) => Number(Boolean(asset.forSale)) + Number(Boolean(asset.tradable)) + Number(Boolean(asset.acceptsSheaf));
      return score(b) - score(a);
    });
  }, [assets, normalizedSellerId, paymentFilter, savedAssetIds, searchTerm, sellerReviewSummaries, sortMode, bitcoinEnabled, barterEnabled, sheafEnabled]);

  const sellerListingCount = useMemo(() => (
    (Array.isArray(assets) ? assets : []).filter((asset) =>
      normalizeUserId(asset.ownedByUserId || asset.ownerId) === normalizedSellerId,
    ).length
  ), [assets, normalizedSellerId]);

  const handleAssetImageClick = (asset) => {
    setTradeError(null);
    setTradeSuccess(null);
    setSheafError(null);
    setSheafSuccess(null);
    setSelectedAsset(asset);
    setShowAddModal(true);
  };

  useEffect(() => {
    if (!selectedAsset?.ownedByUserId && !selectedAsset?.ownerId) {
      setSellerReviewSummary(null);
      setSellerReviewError(null);
      return;
    }

    let isMounted = true;
    const sellerId = selectedAsset.ownedByUserId || selectedAsset.ownerId;
    const fetchSellerSummary = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${apiUrl}/api/MarketplaceReviews/users/${encodeURIComponent(sellerId)}/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`Seller review summary failed (${response.status})`);
        }

        const data = await response.json();
        if (isMounted) {
          setSellerReviewSummary(data);
          setSellerReviewError(null);
        }
      } catch (error) {
        console.error('Error loading seller review summary:', error);
        if (isMounted) {
          setSellerReviewSummary(null);
          setSellerReviewError('Seller reviews are unavailable right now.');
        }
      }
    };

    fetchSellerSummary();

    return () => {
      isMounted = false;
    };
  }, [apiUrl, getAccessTokenSilently, selectedAsset]);

  useEffect(() => {
    const ownerIds = sellerOwnerKey ? sellerOwnerKey.split('|') : [];

    if (!ownerIds.length || !apiUrl) {
      setSellerReviewSummaries({});
      return;
    }

    let isMounted = true;
    const fetchSellerSummaries = async () => {
      try {
        const token = await getAccessTokenSilently();
        const summaries = await Promise.all(ownerIds.map(async (ownerId) => {
          const response = await fetch(`${apiUrl}/api/MarketplaceReviews/users/${encodeURIComponent(ownerId)}/summary`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!response.ok) {
            return [normalizeUserId(ownerId), { reviewCount: 0, averageRating: 0 }];
          }
          const summary = await response.json();
          return [normalizeUserId(ownerId), summary];
        }));

        if (isMounted) {
          setSellerReviewSummaries(Object.fromEntries(summaries));
        }
      } catch (error) {
        console.error('Error loading marketplace seller summaries:', error);
        if (isMounted) {
          setSellerReviewSummaries({});
        }
      }
    };

    fetchSellerSummaries();

    return () => {
      isMounted = false;
    };
  }, [apiUrl, getAccessTokenSilently, sellerOwnerKey]);

  useEffect(() => {
    if (!sellerId || !apiUrl) {
      setStorefrontReviewSummary(null);
      setStorefrontReviewError(null);
      return;
    }

    let isMounted = true;
    const fetchStorefrontSummary = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${apiUrl}/api/MarketplaceReviews/users/${encodeURIComponent(sellerId)}/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`Storefront review summary failed (${response.status})`);
        }

        const data = await response.json();
        if (isMounted) {
          setStorefrontReviewSummary(data);
          setStorefrontReviewError(null);
        }
      } catch (error) {
        console.error('Error loading storefront review summary:', error);
        if (isMounted) {
          setStorefrontReviewSummary(null);
          setStorefrontReviewError('Seller reviews are unavailable right now.');
        }
      }
    };

    fetchStorefrontSummary();

    return () => {
      isMounted = false;
    };
  }, [apiUrl, getAccessTokenSilently, sellerId]);

  const handleCloseModal = () => {
    setSelectedAsset(null);
    setShowAddModal(false);
  };

  const handleTradeItemsSelected = (items) => {
    setTradeError(null);
    setTradeSuccess(null);
    setSelectedItems(items);
    setShowTradeModal(false);
    setShowConfirmationModal(true);
  };

  const handleConfirmTrade = async (gift = false) => {
    setTradeSubmitting(true);
    setTradeError(null);
    setTradeSuccess(null);

    const tradeDTO = {
      SenderId: user.sub,
      RecipientId: selectedAsset.ownerId || selectedAsset.ownedByUserId,
      Status: 'Pending',
      TransactionDetailsConfirmed: false,
      CreatedOn: new Date(),
      IsDeleted: false,
      AssetIds: gift === true ? [selectedAsset.id] : selectedItems.map((item) => item.id),
    };

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${apiUrl}/api/trades`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(tradeDTO),
      });

      if (response.ok) {
        setTradeSuccess(gift === true ? 'Gift requested. The owner must accept before anything is transferred. See your trading history for updates.' : 'Trade offer submitted successfully.');
        setShowConfirmationModal(false);
      } else {
        throw new Error(`Trade submission failed (${response.status})`);
      }
    } catch (error) {
      console.error('Error creating trade:', error);
      setTradeError('Could not submit trade offer. Please retry.');
    } finally {
      setTradeSubmitting(false);
    }
  };

  const handleCreateTradeTestData = async () => {
    setTestDataSubmitting(true);
    setTestDataError(null);
    setTestDataSuccess(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${apiUrl}/api/MarketplaceTestData/two-party-trade`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Marketplace trade test data seed failed (${response.status})`);
      }

      const result = await response.json();
      setTestDataSuccess(
        `Trade test data is ready. Open "Codex trade requested item" and offer "Codex trade offer item".`,
      );
      await refetchAssets();
      return result;
    } catch (error) {
      console.error('Error creating marketplace trade test data:', error);
      setTestDataError('Could not create marketplace trade test data. Please retry.');
      return null;
    } finally {
      setTestDataSubmitting(false);
    }
  };

  const handleBuyWithSheaf = async () => {
    setSheafSubmitting(true);
    setSheafError(null);
    setSheafSuccess(null);

    try {
      const token = await getAccessTokenSilently();
      await purchaseAssetWithSheaf(token, selectedAsset.id);
      setSheafSuccess('Sheaf purchase submitted successfully.');
      setShowAddModal(false);
    } catch (error) {
      console.error('Error purchasing asset with Sheaf:', error);
      setSheafError('Could not complete Sheaf purchase. Please retry.');
    } finally {
      setSheafSubmitting(false);
    }
  };

  const handleListItemSignIn = () => {
    if (typeof loginWithRedirect === 'function') {
      loginWithRedirect({ appState: { returnTo: '/marketplace' } });
    }
  };

  const handleCreateListing = () => {
    if (user?.sub) {
      history.push(`/user-inventory/user/${encodeURIComponent(user.sub)}?action=list-new`);
      return;
    }

    handleListItemSignIn();
  };

  const handleOpenReport = (asset) => {
    if (!user?.sub) {
      if (typeof loginWithRedirect === 'function') {
        loginWithRedirect({ appState: { returnTo: '/marketplace' } });
      }
      return;
    }

    setReportAsset(asset);
    setReportReason('misleading-listing');
    setReportDetails('');
    setReportError(null);
    setReportSuccess(null);
  };

  const handleSubmitReport = async (event) => {
    event.preventDefault();
    if (!reportAsset) return;

    setReportSubmitting(true);
    setReportError(null);
    setReportSuccess(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${apiUrl}/api/MarketplaceReports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          assetId: Number(reportAsset.id),
          reason: reportReason,
          details: reportDetails,
        }),
      });

      if (!response.ok) {
        throw new Error(`Marketplace report failed (${response.status})`);
      }

      setReportSuccess(response.status === 200
        ? 'Thanks. Your existing open report for this listing is still on file.'
        : 'Thanks. A moderation record was created for this listing.');
      setReportDetails('');
      await loadMyMarketplaceReports();
    } catch (error) {
      console.error('Error reporting marketplace listing:', error);
      setReportError('Could not submit the report. Please retry.');
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleWithdrawReport = async (reportId) => {
    if (!reportId || !apiUrl) return;

    setWithdrawingReportId(reportId);
    setReportHistoryError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${apiUrl}/api/MarketplaceReports/${reportId}/withdraw`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Marketplace report withdraw failed (${response.status})`);
      }

      await loadMyMarketplaceReports();
    } catch (error) {
      console.error('Error withdrawing marketplace report:', error);
      setReportHistoryError('Could not withdraw that report. Please retry.');
    } finally {
      setWithdrawingReportId(null);
    }
  };

  const closeReportModal = () => {
    setReportAsset(null);
    setReportError(null);
    setReportSuccess(null);
    setReportSubmitting(false);
  };

  return (
    <main className="marketplace-shell">
      <section className="marketplace-hero" aria-labelledby="marketplace-heading">
        <div className="marketplace-hero-copy">
          <div className="marketplace-kicker">
            <Store className="h-4 w-4" />
            Community marketplace
          </div>
          <h1 id="marketplace-heading">{sellerId ? 'Seller storefront' : 'Buy, sell, barter, and give with Kingdom purpose.'}</h1>
          <p>
            {sellerId
              ? 'Browse this seller’s active marketplace listings, review their completed-exchange feedback, and choose the right next step with confidence.'
              : 'Members can give items away or barter. Groups can also offer items for sale. Share what you have and meet real needs.'}
          </p>
          {sellerId && (
            <div className="marketplace-storefront-summary" aria-label="Seller storefront summary">
              <span>{sellerListingCount} active listing{sellerListingCount === 1 ? '' : 's'}</span>
              <span>
                {storefrontReviewSummary?.reviewCount
                  ? `${Number(storefrontReviewSummary.averageRating || 0).toFixed(1)} / 5 from ${storefrontReviewSummary.reviewCount} review${storefrontReviewSummary.reviewCount === 1 ? '' : 's'}`
                  : storefrontReviewError || 'No completed marketplace reviews yet'}
              </span>
              <Link to="/marketplace">Back to all listings</Link>
            </div>
          )}
          <div className="marketplace-seller-entry" aria-label="List an item in the marketplace">
            <div>
              <span className="marketplace-seller-entry-label">Use what God has placed in your hands</span>
              <strong>Turn one of your goods into a sale, barter, or generous response to a need.</strong>
            </div>
            {user?.sub ? (
              <Link to={`/user-inventory/user/${encodeURIComponent(user.sub)}?action=list-new`} className="marketplace-list-item-link">
                <PackagePlus className="h-4 w-4" />
                Add item from inventory
              </Link>
            ) : (
              <Button
                type="button"
                className="marketplace-list-item-link"
                onClick={handleListItemSignIn}
              >
                <PackagePlus className="h-4 w-4" />
                Add item from inventory
              </Button>
            )}
          </div>
          <div className="marketplace-hero-actions" aria-label="Marketplace capabilities">
            {bitcoinEnabled && <span><Bitcoin className="h-4 w-4" /> BTC purchases</span>}
            {barterEnabled && <span><ArrowLeftRight className="h-4 w-4" /> Barter offers</span>}
            {stripeEnabled && <span><WalletCards className="h-4 w-4" /> Card purchases</span>}
            <Link to="/needs"><HandHeart className="h-4 w-4" /> Answer needs</Link>
            <span><ShieldCheck className="h-4 w-4" /> Authenticated listings</span>
          </div>
          <p className="marketplace-list-item-hint">
            Add the item once, then choose whether it belongs in the marketplace or should be offered directly to someone in need.
          </p>
          <div className="marketplace-deals-basket" aria-label="My deals basket">
            <div className="marketplace-deals-basket-icon">
              <ShoppingBasket className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <span className="marketplace-seller-entry-label">My deals basket</span>
              <strong>Gather saved listings, draft offers, and purchases you need to finish.</strong>
              <p>
                {user?.sub
                  ? `${savedAssetIds.size} saved listing${savedAssetIds.size === 1 ? '' : 's'} ready to revisit.`
                  : 'Sign in to save listings and build trade offers from your inventory.'}
              </p>
            </div>
            <div className="marketplace-deals-basket-actions">
              {user?.sub ? (
                <>
                  <button
                    type="button"
                    className="marketplace-basket-link"
                    onClick={() => setPaymentFilter('saved')}
                  >
                    <Bookmark className="h-4 w-4" />
                    View saved
                  </button>
                  <Link to="/dashboard/trading-history" className="marketplace-basket-link">
                    <ArrowLeftRight className="h-4 w-4" />
                    Deal history
                  </Link>
                </>
              ) : (
                <Button
                  type="button"
                  className="marketplace-basket-link"
                  onClick={handleListItemSignIn}
                >
                  <ShoppingBasket className="h-4 w-4" />
                  Start a basket
                </Button>
              )}
            </div>
          </div>
          {testDataToolsEnabled && (
            <div className="marketplace-test-data-tools">
              <Button
                variant="outline-warning"
                onClick={handleCreateTradeTestData}
                disabled={testDataSubmitting}
              >
                {testDataSubmitting ? 'Creating trade test data...' : 'Create trade test data'}
              </Button>
              {testDataError && (
                <StatusMessage
                  tone="error"
                  title="Trade test data unavailable"
                  message={testDataError}
                />
              )}
              {testDataSuccess && (
                <StatusMessage
                  tone="info"
                  title="Trade test data ready"
                  message={testDataSuccess}
                />
              )}
            </div>
          )}
        </div>
        <div className="marketplace-command-panel" aria-label="Marketplace summary">
          {bitcoinEnabled && <div>
            <span className="marketplace-stat-value">{marketplaceSummary.total}</span>
            <span className="marketplace-stat-label">Active listings</span>
          </div>}
          {barterEnabled && <div>
            <span className="marketplace-stat-value">{marketplaceSummary.btc}</span>
            <span className="marketplace-stat-label">BTC ready</span>
          </div>}
          {sheafEnabled && <div>
            <span className="marketplace-stat-value">{marketplaceSummary.trade}</span>
            <span className="marketplace-stat-label">Open to trade</span>
          </div>}
          <div>
            <span className="marketplace-stat-value">{marketplaceSummary.sheaf}</span>
            <span className="marketplace-stat-label">Sheaf accepted</span>
          </div>
        </div>
      </section>

      {user?.sub && (
        <section className="marketplace-safety-center" aria-labelledby="marketplace-safety-heading">
          <div className="marketplace-safety-copy">
            <span className="marketplace-safety-kicker">
              <Flag className="h-4 w-4" />
              Marketplace safety
            </span>
            <h2 id="marketplace-safety-heading">Your listing concerns stay visible.</h2>
            <p>
              If something looks misleading, unsafe, or off-platform, report it once and track the open record here.
            </p>
          </div>
          <div className="marketplace-report-history" aria-live="polite">
            {reportHistoryLoading && <span className="marketplace-report-history-muted">Loading your reports...</span>}
            {reportHistoryError && <span className="marketplace-report-history-error">{reportHistoryError}</span>}
            {!reportHistoryLoading && !reportHistoryError && reportHistory.length === 0 && (
              <span className="marketplace-report-history-muted">No listing concerns submitted yet.</span>
            )}
            {!reportHistoryLoading && !reportHistoryError && reportHistory.slice(0, 3).map((report) => (
              <article key={report.id} className="marketplace-report-history-row">
                <div>
                  <span>{report.listingName || `Listing #${report.assetId}`}</span>
                  <small>{String(report.reason || 'other').replaceAll('-', ' ')}</small>
                </div>
                <strong>{report.status || 'Open'}</strong>
                {String(report.status || 'Open').toLowerCase() === 'open' && (
                  <button
                    type="button"
                    className="marketplace-report-withdraw"
                    onClick={() => handleWithdrawReport(report.id)}
                    disabled={withdrawingReportId === report.id}
                    aria-label={`Withdraw report for ${report.listingName || `listing ${report.assetId}`}`}
                  >
                    {withdrawingReportId === report.id ? 'Withdrawing...' : 'Withdraw'}
                  </button>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="marketplace-controls" aria-label="Marketplace filters">
        <label className="marketplace-search">
          <span>Search listings</span>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search goods, tools, produce, services..."
          />
        </label>
        <div className="marketplace-filter-group" aria-label="Marketplace filter">
          {[
            ['all', 'All'],
            ['free', 'Free'],
            ...(bitcoinEnabled ? [['btc', 'BTC']] : []),
            ...(barterEnabled ? [['trade', 'Trade']] : []),
            ...(sheafEnabled ? [['sheaf', 'Sheaf']] : []),
            ['saved', 'Saved'],
            ['reviewed', 'Reviewed sellers'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={paymentFilter === value ? 'marketplace-filter is-active' : 'marketplace-filter'}
              onClick={() => setPaymentFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="marketplace-sort">
          <span>Sort</span>
          <select value={sortMode} onChange={(event) => setSortMode(event.target.value)}>
            <option value="featured">Most flexible</option>
            {bitcoinEnabled && <option value="price-low">BTC price low</option>}
            {bitcoinEnabled && <option value="price-high">BTC price high</option>}
            <option value="seller-rating">Seller rating</option>
            <option value="name">Name</option>
          </select>
        </label>
      </section>

      <section className="marketplace-results" aria-live="polite">
        {stripeEnabled && <StripeOffers />}
        <ItemsList
          assets={visibleAssets}
          loading={assetsLoading}
          error={assetsError}
          onRetry={refetchAssets}
          onAssetImageClick={handleAssetImageClick}
          totalCount={assets.length}
          query={searchTerm}
          savedAssetIds={savedAssetIds}
          savingAssetId={savingAssetId}
          onToggleSaved={groupId ? undefined : toggleSavedListing}
          sellerReviewSummaries={sellerReviewSummaries}
          onCreateListing={handleCreateListing}
          onReportListing={handleOpenReport}
        />
        {savedListingsError && (
          <StatusMessage
            tone="error"
            title="Saved listings unavailable"
            message={savedListingsError}
          />
        )}
      </section>

      {selectedAsset && (
        <Modal
          show={showAddModal}
          onHide={handleCloseModal}
          size="xl"
          centered
          contentClassName="marketplace-detail-modal"
        >
          <Modal.Header closeButton className="marketplace-detail-header">
            <Modal.Title>{selectedAsset.productName || selectedAsset.assetName}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="marketplace-detail-body">
            <div className="marketplace-detail-grid">
              <div className="marketplace-detail-gallery">
                <Carousel interval={null}>
                  {[selectedAsset.imageURL, ...(selectedAsset.additionalImages || [])].filter(Boolean).map((image, index) => (
                    <Carousel.Item key={image || index}>
                      <img
                        className="marketplace-detail-image"
                        src={image}
                        alt={`${selectedAsset.productName || selectedAsset.assetName} ${index + 1}`}
                      />
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
              <div className="marketplace-detail-info">
                <div className="marketplace-detail-badges">
                  {selectedAsset.isFree && <Button disabled={tradeSubmitting} onClick={() => handleConfirmTrade(true)}>Request this free item</Button>}
                  {bitcoinEnabled && selectedAsset.ownedByGroupId && selectedAsset.forSale && <span><Bitcoin className="h-4 w-4" /> BTC ready</span>}
                  {barterEnabled && selectedAsset.tradable && <span><ArrowLeftRight className="h-4 w-4" /> Tradeable</span>}
                  {sheafEnabled && selectedAsset.ownedByGroupId && selectedAsset.acceptsSheaf && <span><Sparkles className="h-4 w-4" /> Sheaf accepted</span>}
                </div>
                <p>{selectedAsset.productDescription || selectedAsset.assetDescription || 'No description provided yet.'}</p>
                <dl className="marketplace-detail-terms">
                  {bitcoinEnabled && selectedAsset.ownedByGroupId && selectedAsset.forSale && (
                    <div>
                      <dt>BTC price</dt>
                      <dd>{selectedAsset.salePrice} BTC</dd>
                    </div>
                  )}
                  {sheafEnabled && selectedAsset.ownedByGroupId && selectedAsset.acceptsSheaf && (
                    <div>
                      <dt>Sheaf price</dt>
                      <dd>{selectedAsset.sheafPrice} Sheaf</dd>
                    </div>
                  )}
                  <div>
                    <dt>Trade offers</dt>
                    <dd>{selectedAsset.tradable ? 'Open' : 'Not accepted'}</dd>
                  </div>
                </dl>
                <div className="marketplace-detail-reliability">
                  <ShieldCheck className="h-5 w-5" />
                  <span>Authenticated marketplace action. Review the listing and exchange terms before submitting.</span>
                </div>
                <div className="marketplace-seller-confidence" aria-label="Seller confidence">
                  <div>
                    <span className="marketplace-seller-confidence-label">Seller confidence</span>
                    <strong>
                      {sellerReviewSummary?.reviewCount
                        ? `${Number(sellerReviewSummary.averageRating || 0).toFixed(1)} / 5`
                        : 'New seller'}
                    </strong>
                  </div>
                  <p>
                    {sellerReviewSummary?.reviewCount
                      ? `${sellerReviewSummary.reviewCount} marketplace review${sellerReviewSummary.reviewCount === 1 ? '' : 's'} from completed trades or purchases.`
                      : sellerReviewError || 'No completed marketplace reviews yet. Start with clear terms and use the protected transaction flow.'}
                  </p>
                  {(selectedAsset.ownedByUserId || selectedAsset.ownerId) && (
                    <Link
                      to={`/marketplace/sellers/${encodeURIComponent(selectedAsset.ownedByUserId || selectedAsset.ownerId)}`}
                      className="marketplace-seller-link"
                      onClick={handleCloseModal}
                    >
                      View seller storefront
                    </Link>
                  )}
                  <button
                    type="button"
                    className="marketplace-report-inline"
                    onClick={() => handleOpenReport(selectedAsset)}
                  >
                    <Flag className="h-4 w-4" />
                    Report a concern about this listing
                  </button>
                </div>
              </div>
            </div>
            {tradeError && (
              <StatusMessage
                tone="error"
                title="Could not submit trade offer"
                message={tradeError}
              />
            )}
            {tradeSuccess && (
              <StatusMessage
                tone="info"
                title="Trade offer submitted"
                message={tradeSuccess}
              />
            )}
            {sheafError && (
              <StatusMessage
                tone="error"
                title="Could not complete Sheaf purchase"
                message={sheafError}
              />
            )}
            {sheafSuccess && (
              <StatusMessage
                tone="info"
                title="Sheaf purchase submitted"
                message={sheafSuccess}
              />
            )}
          </Modal.Body>
          <Modal.Footer className="marketplace-detail-footer">
            <div className="marketplace-detail-tradable">
              <span>Tradable</span>
              <FontAwesomeIcon
                icon={selectedAsset.tradable ? faCheck : faTimes}
                className={selectedAsset.tradable ? 'text-success' : 'text-danger'}
              />
              {barterEnabled && selectedAsset.tradable && (
                <Button
                  variant="outline-warning"
                  onClick={() => setShowTradeModal(true)}
                  className="marketplace-secondary-action"
                >
                  Offer a Trade
                </Button>
              )}
            </div>
            <div className="marketplace-detail-actions">
              {!groupId && (
                <Button
                  variant="outline-warning"
                  onClick={() => toggleSavedListing(selectedAsset.id)}
                  disabled={savingAssetId === Number(selectedAsset.id)}
                  className="marketplace-secondary-action"
                  aria-pressed={savedAssetIds.has(Number(selectedAsset.id))}
                >
                  <Bookmark className="h-4 w-4" />
                  {savedAssetIds.has(Number(selectedAsset.id)) ? 'Saved' : 'Save listing'}
                </Button>
              )}
              {bitcoinEnabled && selectedAsset.ownedByGroupId && selectedAsset.forSale && (
                <>
                  <span>
                    <WalletCards className="h-4 w-4" /> <strong>{selectedAsset.salePrice} BTC</strong>
                  </span>
                  <Button
                    variant="success"
                    onClick={() => setShowBuyModal(true)}
                    className="marketplace-primary-action"
                  >
                    Buy with BTC
                  </Button>
                </>
              )}
              {sheafEnabled && selectedAsset.ownedByGroupId && selectedAsset.acceptsSheaf && (
                <>
                  <span>
                    <Sparkles className="h-4 w-4" /> <strong>{selectedAsset.sheafPrice} Sheaf</strong>
                  </span>
                  <Button
                    variant="success"
                    onClick={handleBuyWithSheaf}
                    disabled={sheafSubmitting}
                    className="marketplace-primary-action"
                  >
                    {sheafSubmitting ? 'Buying with Sheaf...' : 'Buy with Sheaf'}
                  </Button>
                </>
              )}
            </div>
          </Modal.Footer>
        </Modal>
      )}

      <Modal
        show={Boolean(reportAsset)}
        onHide={closeReportModal}
        centered
        contentClassName="marketplace-flow-modal"
      >
        <form onSubmit={handleSubmitReport}>
          <Modal.Header closeButton className="marketplace-flow-header">
            <Modal.Title>
              <span className="marketplace-flow-kicker">
                <Flag className="h-4 w-4" />
                Marketplace safety
              </span>
              Report a listing concern
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="marketplace-flow-body">
            <div className="marketplace-report-panel">
              <p>
                Tell us what looks wrong with <strong>{reportAsset?.productName || reportAsset?.assetName || 'this listing'}</strong>.
                Reports create a durable moderation record tied to the listing and your authenticated account.
              </p>
              <label>
                <span>Reason</span>
                <select value={reportReason} onChange={(event) => setReportReason(event.target.value)}>
                  <option value="misleading-listing">Misleading listing details</option>
                  <option value="unsafe-item">Unsafe or illegal item</option>
                  <option value="payment-concern">Payment or exchange concern</option>
                  <option value="seller-concern">Seller conduct concern</option>
                  <option value="prohibited-item">Prohibited item</option>
                  <option value="other">Something else</option>
                </select>
              </label>
              <label>
                <span>What should moderators know?</span>
                <textarea
                  value={reportDetails}
                  onChange={(event) => setReportDetails(event.target.value)}
                  minLength={8}
                  maxLength={1000}
                  rows={5}
                  required
                  placeholder="Describe the concern without sharing private payment details."
                />
              </label>
              {reportError && (
                <StatusMessage tone="error" title="Report not submitted" message={reportError} />
              )}
              {reportSuccess && (
                <StatusMessage tone="info" title="Report submitted" message={reportSuccess} />
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="marketplace-flow-footer">
            <Button type="button" variant="outline-light" onClick={closeReportModal}>
              Close
            </Button>
            <Button type="submit" className="marketplace-primary-action" disabled={reportSubmitting || reportDetails.trim().length < 8}>
              {reportSubmitting ? 'Submitting report...' : 'Submit report'}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {bitcoinEnabled && <BuyNowModal
        show={showBuyModal}
        handleClose={() => setShowBuyModal(false)}
        userId={user?.sub}
        asset={selectedAsset}
      />}

      {selectedAsset && (
        <TradeSelectionModal
          show={showTradeModal}
          onHide={() => setShowTradeModal(false)}
          userId={user?.sub}
          onItemsSelected={handleTradeItemsSelected}
        />
      )}

      {selectedAsset && (
        <TradeConfirmationModal
          show={showConfirmationModal}
          onHide={() => setShowConfirmationModal(false)}
          selectedItems={selectedItems}
          selectedAsset={selectedAsset}
          onConfirm={handleConfirmTrade}
          confirming={tradeSubmitting}
          error={tradeError}
          onEdit={() => {
            setShowConfirmationModal(false);
            setShowTradeModal(true);
          }}
        />
      )}
    </main>
  );
};

export default ForSalePage;
